import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";

import { Box, Paper } from "@mui/material";

import { GridRowSelectionModel } from "@mui/x-data-grid";

import AddAdditionalItemModal from "./AddAdditionalItemModal";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { AdditionalItem, MassiveAdditionalItem } from "types";
import { ADDITIONAL_ITEMS_URL as BASE_URL } from "constants/urls";

import { ENTITY } from "constants/entities";
import EditAdditionalItemModal from "./EditAdditionalItemModal";
import Loader from "../Loader";
import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import { useSession } from "providers/SessionProvider";
import { STATUS_CODES } from "constants/index";
import { useToast } from "hooks/useToast";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { getClientName } from "helpers/getClientName";
import { BiMoney } from "react-icons/bi";
import EditMassiveAdditionalItemModal from "./EditMassiveAdditionalItemModal";
import { exportToExcel } from "helpers/exportToExcel";

export default function AdditionalItemsList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const [editMassiveOpen, setEditMassiveOpen] = useState<boolean>(false);

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const {
    additionalItems,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    users,
  } = useSession();

  const { showSnackBar } = useToast();

  const {
    createData: createAdditionalItem,
    deleteData: deleteAdditionalItem,
    updateData: updateAdditionalItem,
    loading,
    firstLoad,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<AdditionalItem>(BASE_URL);

  const { updateData: updateMassiveAdditionalItem, loading: massiveLoading } =
    useCrud<MassiveAdditionalItem>(BASE_URL);

  const additionalItemsExcelColumns = {
    Descripción: (ai: AdditionalItem & { client_name?: string }) =>
      ai.description ?? "-",

    Cliente: (ai: AdditionalItem & { client_name?: string }) =>
      ai.client_id === null ? "TODOS" : (ai.client_name ?? "-"),

    Precio: (ai: AdditionalItem) => ai.price ?? 0,
    Tiempo: (ai: AdditionalItem) => ai.time_minutes || "-",
    "Creado por": (ai: AdditionalItem & { created_by_name?: string }) =>
      ai.created_by_name ?? "-",
  } satisfies Record<string, (row: any) => any>;

  const handleExportAdditionalItemsExcel = () => {
    exportToExcel<AdditionalItem>({
      data: filteredAdditionalItems,
      columns: additionalItemsExcelColumns,
      sheetName: "Adicionales",
      fileNamePrefix: "Items_adicionales",
      headerRows: [
        ["Listado de items adicionales"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const enrichedAdditionalItems = useMemo(() => {
    return additionalItems.map((item) => {
      const client = item.client;
      const client_name =
        item.client_id === null ? "TODOS" : getClientName(client);

      return {
        ...item,
        client_name,
        created_by_name: (
          item as AdditionalItem & { created_by?: number | null }
        ).created_by
          ? getUserFullName(
              (item as AdditionalItem & { created_by?: number | null })
                .created_by,
              users,
            )
          : "SISTEMA",
      };
    });
  }, [additionalItems, users]);

  const {
    filterText,
    setFilterText,
    filteredData: filteredAdditionalItems,
  } = useFilter<
    AdditionalItem & { client_name: string; created_by_name: string }
  >({
    data: enrichedAdditionalItems,
    fields: gridStructure[ENTITY.ADDITIONAL_ITEMS].fields.map(({ field }) =>
      field === "client" ? { field: "client_name" } : { field },
    ),
  });

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteAdditionalItem(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.ADDITIONAL_ITEMS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    setSelected([]);
  };

  const handleCreate = async (newAdditionalItem: AdditionalItem) => {
    const response = await createAdditionalItem(
      {
        ...newAdditionalItem,
        time_minutes: newAdditionalItem.time_minutes || null,
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.ADDITIONAL_ITEMS, response.data as AdditionalItem);
    }
  };

  const handleEdit = async (additionalItemToEdit: AdditionalItem) => {
    const response = await updateAdditionalItem(
      additionalItemToEdit.id,
      {
        ...additionalItemToEdit,
        time_minutes: additionalItemToEdit.time_minutes || null,
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.ADDITIONAL_ITEMS, additionalItemToEdit);
    }
    setSelected([]);
  };

  const handleMassiveEdit = async (
    additionalItemToEdit: MassiveAdditionalItem,
  ) => {
    const response = await updateMassiveAdditionalItem(
      "massive",
      {
        ...additionalItemToEdit,
        id: selected.map((s) => parseInt(s.toString())),
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      for (const item of response.data) {
        updateSessionData(ENTITY.ADDITIONAL_ITEMS, item);
      }
    }
    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader
            isFirstLoad={firstLoad}
            isLoading={loading || massiveLoading}
          />
          <AddAdditionalItemModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.ADDITIONAL_ITEMS}
            values={selected.map((item) => {
              const value = additionalItems.find(
                (ai) => ai.id === item,
              )?.description;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditAdditionalItemModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={
                additionalItems.find(
                  (ai) => ai.id === selected[0],
                ) as AdditionalItem
              }
              onEdit={handleEdit}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                massiveEditIcon={<BiMoney />}
                handleMassiveEditModalOpen={() =>
                  setEditMassiveOpen(!editMassiveOpen)
                }
                onExportExcel={handleExportAdditionalItemsExcel}
              />
              <CustomGrid<AdditionalItem>
                structure={gridStructure[ENTITY.ADDITIONAL_ITEMS]}
                rows={filteredAdditionalItems}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
      <EditMassiveAdditionalItemModal
        open={editMassiveOpen}
        handleOpen={() => setEditMassiveOpen(!editMassiveOpen)}
        value={{
          id: [],
          price: 0,
          type: "$",
        }}
        onEdit={handleMassiveEdit}
      />
    </>
  );
}
